/**
 * Sidebar Component - Navegación lateral principal
 *
 * QUÉ HACE:
 * - Renderiza el menú de navegación lateral con soporte para roles y permisos
 * - Maneja estado colapsado/expandido en desktop y overlay en mobile
 * - Filtra items de navegación según permisos del usuario
 *
 * QUÉ INVOCA: usePermissions, useLogout (hooks compartidos)
 * QUÉ EXPORTA: Sidebar (default)
 *
 * QUÉ NO DEBE HACER:
 * - NO debe duplicar lógica de permisos (usa usePermissions)
 * - NO debe duplicar lógica de logout (usa useLogout)
 * - NO debe inyectar CSS inline (usa clases de index.css)
 */

import {
  NavLink,
  useLocation,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import {
  X,
  LogOut,
  ChevronRight,
  ChevronDown,
  Settings,
  Menu,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useLogout, usePermissions } from "@/hooks";
import { useBuzonEquipoEnabled } from "@/features/personal/buzon/useBuzonEquipoEnabled";
import { NAV_ITEMS, NavItem } from "./navigation.config";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { UserSettingsPanel, type PanelView } from "./UserSettingsPanel";
import { navigationGuard } from "@/lib/utils/navigationGuard";
import { preloadRoute } from "@/lib/routePreloads";
import { SAFE_AREA } from "@/lib/nativeShell";
import { useSidebarExpand } from "./useSidebarExpand";
import { STORAGE_SETTINGS_OPEN_EVENT } from "@/lib/persistence/storageUsage";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  /** Modo demo para landing: no navega, no hace logout y usa ruta activa controlada. */
  previewMode?: boolean;
  previewActivePath?: string;
  onPreviewNavigate?: (path: string, label: string) => void;
  onPreviewExpandChange?: (expanded: boolean) => void;
  previewUser?: {
    nombre: string;
    email: string;
    role: string;
  };
}

const Sidebar = ({
  isOpen,
  onClose,
  previewMode = false,
  previewActivePath,
  onPreviewNavigate,
  onPreviewExpandChange,
  previewUser,
}: SidebarProps) => {
  const { user } = useAuthStore();
  const { handleLogout } = useLogout();
  const { canAccess } = usePermissions();
  const navigate = useNavigate();
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsInitialView, setSettingsInitialView] =
    useState<PanelView>("main");
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const lastCajaFecha = useRef<string | null>(null);
  const lastCajaRange = useRef<{
    desde: string;
    hasta: string;
    hora?: string;
    label?: string;
  } | null>(null);

  // Abrir Ajustes → una sub-vista concreta desde cualquier parte (banner de memoria).
  useEffect(() => {
    const openSettings = (e: Event) => {
      const detail = (e as CustomEvent<{ view?: PanelView }>).detail;
      setSettingsInitialView(detail?.view ?? "main");
      setShowSettings(true);
    };
    window.addEventListener(STORAGE_SETTINGS_OPEN_EVENT, openSettings);
    return () =>
      window.removeEventListener(STORAGE_SETTINGS_OPEN_EVENT, openSettings);
  }, []);

  const notifyPreviewExpand = (expanded: boolean) => {
    onPreviewExpandChange?.(expanded);
  };

  const {
    isLg,
    canHoverExpand,
    isCoarsePointer,
    isExpanded,
    isCollapsedDesktop,
    expandByHover,
    expandManually,
    toggleExpanded,
    collapseExpanded,
    collapseOnHoverLeave,
    showTouchBackdrop,
  } = useSidebarExpand({
    pathname: location.pathname,
    onExpandChange: previewMode ? notifyPreviewExpand : undefined,
  });

  const dismissSidebar = () => {
    collapseExpanded();
    onClose();
  };

  // Auto-abre el submenú si la ruta actual es el padre o una sub-ruta,
  // y lo cierra automáticamente al salir de esa sección
  useEffect(() => {
    const activeParent = NAV_ITEMS.find(
      (item) =>
        (item.children &&
          item.children.length > 0 &&
          location.pathname.startsWith(item.path)) ||
        item.children?.some((child) =>
          location.pathname.startsWith(child.path),
        ),
    );
    if (activeParent) {
      setOpenSubmenu(activeParent.path);
    } else {
      setOpenSubmenu(null);
    }
  }, [location.pathname]);

  // Recuerda la última fecha/rango activo en rutas de caja.
  // Si el usuario está en /caja/* sin ?fecha=, la considera eliminada.
  useEffect(() => {
    if (location.pathname.startsWith("/caja/")) {
      lastCajaFecha.current = searchParams.get("fecha");
      lastCajaRange.current =
        searchParams.get("desde") && searchParams.get("hasta")
          ? {
              desde: searchParams.get("desde") as string,
              hasta: searchParams.get("hasta") as string,
              hora: searchParams.get("hora") ?? undefined,
              label: searchParams.get("label") ?? undefined,
            }
          : null;
    }
  }, [location.pathname, searchParams]);

  // Usa fecha del URL actual si existe; si no, la última recordada
  const activeFecha = searchParams.get("fecha") || lastCajaFecha.current;
  const activeRango = lastCajaRange.current;

  const resolveChildPath = (childPath: string) => {
    if (childPath.startsWith("/caja/")) {
      if (activeRango) {
        const params = new URLSearchParams();
        params.set("desde", activeRango.desde);
        params.set("hasta", activeRango.hasta);
        if (activeRango.hora) params.set("hora", activeRango.hora);
        if (activeRango.label) params.set("label", activeRango.label);
        return `${childPath}?${params.toString()}`;
      }
      if (activeFecha) return `${childPath}?fecha=${activeFecha}`;
    }
    return childPath;
  };

  const menuItems: NavItem[] = previewMode
    ? NAV_ITEMS.filter(
        (item) =>
          !["/mis-tareas", "/mi-asistencia", "/mi-nomina"].includes(
            item.path,
          ) && !item.requiresBuzonEquipo,
      )
    : NAV_ITEMS;
  const buzonEquipoEnabled = useBuzonEquipoEnabled();
  const isPreviewDesktop = previewMode && isLg;

  const currentPath = previewMode
    ? previewActivePath || "/dashboard"
    : location.pathname;
  const effectiveUser = previewMode
    ? {
        nombre: previewUser?.nombre ?? "Juan Perez",
        email: previewUser?.email ?? "juan.perez@altenot.com",
        role: previewUser?.role ?? "administrador",
      }
    : user;

  /** Solo escritorio colapsado (rail); en móvil el drawer siempre es ancho completo. */
  const collapsedDesktop = previewMode
    ? isPreviewDesktop
      ? isCollapsedDesktop
      : false
    : isCollapsedDesktop;
  const showNavLabels = !collapsedDesktop;
  /** Abrir / expandir: más lento. Cerrar / rail: rápido (evita sensación rara con la X). */
  const motionExpanded = isOpen || isExpanded;

  const toggleDesktopExpand = () => {
    if (!isLg && !isPreviewDesktop) return;
    toggleExpanded();
  };

  const mobileDrawerInteractive = previewMode
    ? isPreviewDesktop || isOpen
    : isLg || isOpen;

  return (
    <>
      {showTouchBackdrop && !previewMode && (
        <button
          type="button"
          aria-label="Cerrar menú lateral"
          className="fixed inset-0 z-[56] border-0 bg-black/50 dark:bg-black/70"
          onClick={dismissSidebar}
        />
      )}

      <aside
        onPointerEnter={(e) => {
          if (!isLg && !isPreviewDesktop) return;
          if (!collapsedDesktop) return;
          if (!canHoverExpand || isCoarsePointer) return;
          if (e.pointerType !== "mouse") return;
          expandByHover();
        }}
        onPointerLeave={(e) => {
          if (!canHoverExpand || isCoarsePointer) return;
          if (e.pointerType !== "mouse") return;
          if (!isLg && !isPreviewDesktop) return;
          collapseOnHoverLeave();
        }}
        className={`
        ${SAFE_AREA.sidebar} ${previewMode ? (isPreviewDesktop ? "relative inset-0 z-10 h-full" : "absolute inset-y-0 left-0 z-[60]") : "fixed inset-y-0 left-0 z-[57]"} flex flex-col border-r border-gray-200 bg-white shadow-lg transition-[width,transform,opacity] dark:border-slate-700 dark:bg-slate-900 touch-manipulation
        ${previewMode ? "duration-200 ease-in-out" : motionExpanded ? "duration-[300ms] ease-out" : "duration-150 ease-in"}
        ${previewMode ? "" : "lg:static lg:inset-0"}
        ${previewMode ? (isPreviewDesktop ? "translate-x-0" : isOpen ? "translate-x-0" : "-translate-x-full") : isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        ${previewMode ? (isPreviewDesktop ? (isCollapsedDesktop ? "w-20" : "w-64") : "w-64") : isCollapsedDesktop ? "w-20" : "w-64"}
        ${previewMode && !isPreviewDesktop && !isOpen ? "opacity-0 pointer-events-none" : mobileDrawerInteractive ? "opacity-100 pointer-events-auto" : "opacity-100 pointer-events-none lg:pointer-events-auto"}
      `}
      >
        {/* Header — marca arriba; hamburguesa solo en rail colapsado */}
        <div
          className={`flex items-center border-b border-gray-100 px-3 dark:border-slate-700 sm:px-4 ${
            showNavLabels
              ? "min-h-[3rem] gap-2 justify-between py-2 max-lg:min-h-[2.75rem] max-lg:py-1.5"
              : "min-h-[3.5rem] justify-center py-3"
          }`}
        >
          {showNavLabels ? (
            <>
              <div className="flex min-w-0 flex-1 items-center gap-2.5">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                  aria-hidden
                >
                  <img
                    src="/favicon.svg"
                    alt=""
                    draggable={false}
                    className="h-9 w-9 object-contain"
                  />
                </div>
                <div className="min-w-0">
                  <h1 className="truncate text-lg font-bold leading-tight text-primary-600 dark:text-primary-400 sm:text-xl">
                    ALTENOT
                  </h1>
                  <p className="truncate text-[10px] text-gray-500 dark:text-gray-400">
                    Control Total
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={dismissSidebar}
                aria-label="Cerrar menú"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors duration-200 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-800 lg:hidden"
              >
                <X className="h-5 w-5" strokeWidth={2} aria-hidden />
              </button>
              <button
                type="button"
                onClick={toggleDesktopExpand}
                aria-label="Contraer menú lateral"
                aria-expanded={isExpanded}
                className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors duration-200 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-800 lg:flex"
              >
                <Menu className="h-5 w-5" strokeWidth={2} aria-hidden />
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 py-0.5">
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 transition-colors duration-150 hover:bg-gray-100 active:bg-gray-200 dark:text-gray-300 dark:hover:bg-slate-800 dark:active:bg-slate-700 touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
                aria-label={
                  isExpanded ? "Contraer menú lateral" : "Expandir menú lateral"
                }
                aria-expanded={isExpanded}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDesktopExpand();
                }}
              >
                <Menu className="h-5 w-5" strokeWidth={2} aria-hidden />
              </button>
              <div
                className="flex h-10 w-10 items-center justify-center rounded-xl"
                title="ALTENOT"
                aria-hidden
              >
                <img
                  src="/favicon.svg"
                  alt=""
                  draggable={false}
                  className="h-9 w-9 object-contain"
                />
              </div>
            </div>
          )}
        </div>

        {/* La lista se desplaza con barra invisible; el pie (tema/salir) queda fijo abajo */}
        <nav className="flex min-h-0 flex-1 flex-col px-2 py-1.5">
          <div className="scrollbar-none flex min-h-0 flex-1 flex-col gap-px overflow-y-auto overflow-x-hidden overscroll-contain pr-0.5">
            {menuItems.map((item) => {
              if (
                !previewMode &&
                !canAccess(item.roles, item.permissionKey, item.excludeRoles)
              )
                return null;
              if (
                !previewMode &&
                item.requiresBuzonEquipo &&
                !buzonEquipoEnabled
              )
                return null;
              const hasChildren = !!(item.children && item.children.length > 0);
              const isSubmenuOpen = openSubmenu === item.path;
              const isPreviewActive =
                currentPath === item.path ||
                (item.children ?? []).some((c) =>
                  currentPath.startsWith(c.path),
                );
              /** En preview todos los NavLink van a "#"; RR marcaría isActive en todos — solo usamos la ruta simulada. */
              const isParentNavActive = (isActive: boolean) =>
                previewMode ? isPreviewActive : isActive || isPreviewActive;

              return (
                <div key={item.path}>
                  <NavLink
                    to={previewMode ? "#" : item.path}
                    onMouseEnter={() => preloadRoute(item.path)}
                    onFocus={() => preloadRoute(item.path)}
                    onClick={(e) => {
                      if (previewMode) {
                        e.preventDefault();
                        onPreviewNavigate?.(item.path, item.label);
                        if (hasChildren) setOpenSubmenu(item.path);
                        return;
                      }
                      e.preventDefault();

                      if (collapsedDesktop && hasChildren) {
                        expandManually();
                        setOpenSubmenu(item.path);
                        return;
                      }

                      const allowed = navigationGuard.check(() => {
                        navigate(item.path);
                        dismissSidebar();
                      });
                      if (!allowed) return;
                      if (hasChildren) setOpenSubmenu(item.path);
                    }}
                    className={({ isActive }) =>
                      `group flex w-full items-center rounded-lg transition-all duration-200 ease-out ${
                        showNavLabels
                          ? "gap-1.5 px-2 py-1 max-lg:py-0.5"
                          : "px-2.5 py-1.5"
                      } ${
                        isParentNavActive(isActive)
                          ? "bg-primary-600 text-white shadow-md"
                          : `text-gray-600 sidebar-item hover:bg-gray-100 dark:hover:bg-slate-800 ${
                              showNavLabels ? "hover:translate-x-1" : ""
                            }`
                      } ${!showNavLabels ? "justify-center" : ""}`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          size={showNavLabels ? 16 : 18}
                          className={`shrink-0 ${isParentNavActive(isActive) ? "text-white" : "text-gray-500 sidebar-item group-hover:text-gray-700"}`}
                        />
                        <span
                          className={`min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-semibold ${
                            showNavLabels
                              ? "max-w-[11rem] opacity-100"
                              : "hidden"
                          }`}
                        >
                          {item.label}
                        </span>
                        {showNavLabels && hasChildren && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setOpenSubmenu(isSubmenuOpen ? null : item.path);
                            }}
                            className={`ml-auto rounded-md p-1 transition-colors duration-150 ${
                              isParentNavActive(isActive)
                                ? "hover:bg-primary-500 text-white"
                                : "hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400 dark:text-gray-500"
                            }`}
                          >
                            {isSubmenuOpen ? (
                              <ChevronDown size={13} />
                            ) : (
                              <ChevronRight size={13} />
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </NavLink>

                  {showNavLabels && hasChildren && isSubmenuOpen && (
                    <div
                      className="ml-4 mt-0 border-l-2 border-primary-100 py-0 pl-1.5 dark:border-primary-900/40"
                      role="group"
                      aria-label={`Submenú ${item.label}`}
                    >
                      {(item.children ?? []).map((child) => {
                        if (
                          !previewMode &&
                          !canAccess(
                            child.roles,
                            child.permissionKey,
                            child.excludeRoles,
                          )
                        )
                          return null;
                        const isChildPreviewActive =
                          currentPath === child.path ||
                          currentPath.startsWith(`${child.path}/`);
                        const isChildNavActive = (isActive: boolean) =>
                          previewMode ? isChildPreviewActive : isActive;
                        return (
                          <NavLink
                            key={child.path}
                            to={
                              previewMode ? "#" : resolveChildPath(child.path)
                            }
                            onMouseEnter={() =>
                              preloadRoute(resolveChildPath(child.path))
                            }
                            onFocus={() =>
                              preloadRoute(resolveChildPath(child.path))
                            }
                            onClick={(e) => {
                              if (previewMode) {
                                e.preventDefault();
                                onPreviewNavigate?.(child.path, child.label);
                                return;
                              }
                              e.preventDefault();
                              const targetPath = resolveChildPath(child.path);
                              const allowed = navigationGuard.check(() => {
                                navigate(targetPath);
                                dismissSidebar();
                              });
                              if (!allowed) return;
                            }}
                            className={({ isActive }) =>
                              `block rounded-md px-1.5 py-0.5 text-[9.5px] font-medium leading-snug transition-colors duration-150 ${
                                isChildNavActive(isActive)
                                  ? "bg-primary-50 text-primary-700 dark:bg-primary-900/35 dark:text-primary-300"
                                  : "text-gray-600 sidebar-item hover:bg-gray-50 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-slate-800/80 dark:hover:text-gray-200"
                              }`
                            }
                          >
                            {child.label}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!previewMode && (
            <div className="mt-2 shrink-0 space-y-1.5 border-t border-gray-100 pt-2 dark:border-slate-700/80">
              <ThemeToggle expanded={showNavLabels} compact />
              <button
                type="button"
                onClick={handleLogout}
                title={!showNavLabels ? "Cerrar Sesión" : ""}
                className={`group flex w-full items-center gap-2 rounded-lg px-2.5 py-1 text-red-600 transition-colors duration-200 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 ${!showNavLabels ? "justify-center py-1.5" : ""}`}
              >
                <LogOut size={15} className="shrink-0" />
                {showNavLabels && (
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[10px] font-medium text-red-600 dark:text-red-400">
                    Cerrar Sesión
                  </span>
                )}
              </button>
            </div>
          )}
        </nav>

        {/* User Profile */}
        <div
          className={`${SAFE_AREA.sidebarFooter} relative border-t border-gray-200 bg-gray-50 p-2.5 dark:border-slate-700 dark:bg-slate-800/50 sm:p-3`}
        >
          {/* Settings Panel (posicionado sobre el perfil) */}
          {!previewMode && (
            <UserSettingsPanel
              isOpen={showSettings}
              onClose={() => setShowSettings(false)}
              sidebarExpanded={showNavLabels}
              initialView={settingsInitialView}
              onInitialViewConsumed={() => setSettingsInitialView("main")}
            />
          )}

          {/* User Info Row */}
          <div
            className={`flex items-center gap-3 ${!showNavLabels ? "justify-center" : ""}`}
          >
            {/* Avatar — clic abre el panel */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!previewMode) setShowSettings((v) => !v);
              }}
              title="Configuración de cuenta"
              className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border-2 border-primary-300 bg-primary-600 text-sm font-bold text-white transition-all hover:ring-2 hover:ring-primary-400 hover:ring-offset-1 active:scale-95 dark:border-primary-500 dark:hover:ring-offset-slate-800 sm:h-10 sm:w-10 sm:text-base"
            >
              {effectiveUser?.email?.charAt(0).toUpperCase() || "U"}
            </button>

            {showNavLabels && (
              <>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-gray-900 dark:text-gray-100 sm:text-sm">
                    {effectiveUser?.nombre ||
                      effectiveUser?.email?.split("@")[0]}
                  </p>
                  <p className="truncate text-[10px] capitalize text-gray-500 dark:text-gray-400 sm:text-xs">
                    Rol: {effectiveUser?.role}
                  </p>
                </div>
                {/* Gear icon */}
                <button
                  onClick={() => {
                    if (!previewMode) setShowSettings((v) => !v);
                  }}
                  title="Configuración avanzada"
                  className={`flex-shrink-0 rounded-lg p-1.5 transition-all duration-150 active:scale-90 sm:rounded-xl sm:p-2
                  ${
                    !previewMode && showSettings
                      ? "bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-400"
                      : "text-gray-400 hover:bg-gray-200 hover:text-gray-700 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-300"
                  }`}
                >
                  <Settings
                    size={15}
                    className={!previewMode && showSettings ? "rotate-45" : ""}
                    style={{ transition: "transform 0.3s ease" }}
                  />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
